import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Divider,
  Fade,
  Zoom,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ScienceIcon from '@mui/icons-material/Science';
import FactoryIcon from '@mui/icons-material/Factory';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import BuildIcon from '@mui/icons-material/Build';

import jobberJunior from '../assets/infrastructure/Jobber-Junior.jpg';
import latheMachines from '../assets/infrastructure/LATHE MACHINES.jpg';
import lt2lm1200 from '../assets/infrastructure/LT-2-LM-1200-1.jpg';
import lt2lm500 from '../assets/infrastructure/LT-2-LM-500-1.jpg';
import mitrMachine from '../assets/infrastructure/M1TR MACHINE.jpg';
import mcv350 from '../assets/infrastructure/MCV-350.jpg';
import mcv450sv from '../assets/infrastructure/MCV-450_SV.jpg';
import mcv550 from '../assets/infrastructure/MCV-550.jpg';
import robocutAc400ic from '../assets/infrastructure/ROBOCUT-A-c400iC.png';
import tigWelding from '../assets/infrastructure/TIG WELDING MACHINE.jpg';
import ace1060v from '../assets/infrastructure/ace 1060v.jpg';
import acelm450 from '../assets/infrastructure/ace lm 450.jpg';
import acelm600 from '../assets/infrastructure/ace lm 600.jpg';
import cylindricalGrinding from '../assets/infrastructure/cylindrical grinding machine.jpg';
import heavyDutyCylindrical from '../assets/infrastructure/heavy duty cylindrical griding machine.jpg';
import heavyDutySurface from '../assets/infrastructure/heavy duty surface grinder machine.jpg';
import hydralicPress from '../assets/infrastructure/hydralic press machine.jpeg';

const machines = [
  {
    image: jobberJunior,
    name: 'Jobber Junior',
    specs: ['High-precision drilling', 'Automated tool change', 'Digital display']
  },
  {
    image: latheMachines,
    name: 'Lathe Machines',
    specs: ['Multi-axis control', 'High RPM capacity', 'Precision threading']
  },
  {
    image: lt2lm1200,
    name: 'LT-2 LM 1200',
    specs: ['Large workpiece capacity', 'CNC control', 'High accuracy']
  },
  {
    image: lt2lm500,
    name: 'LT-2 LM 500',
    specs: ['Compact design', 'Versatile operations', 'Quick setup']
  },
  {
    image: mitrMachine,
    name: 'MITR Machine',
    specs: ['Precision cutting', 'Angle adjustment', 'Safety features']
  },
  {
    image: mcv350,
    name: 'MCV 350',
    specs: ['Vertical machining', '3-axis control', 'Tool magazine']
  },
  {
    image: mcv450sv,
    name: 'MCV 450 SV',
    specs: ['Enhanced stability', 'Large work envelope', 'Advanced controls']
  },
  {
    image: mcv550,
    name: 'MCV 550',
    specs: ['Heavy-duty cutting', 'High power spindle', 'Rigid construction']
  },
  {
    image: robocutAc400ic,
    name: 'Robocut A-C400iC',
    specs: ['Wire EDM technology', 'Automatic threading', 'High precision']
  },
  {
    image: tigWelding,
    name: 'TIG Welding Machine',
    specs: ['Precision welding', 'Temperature control', 'Multiple materials']
  },
  {
    image: ace1060v,
    name: 'ACE 1060V',
    specs: ['Vertical machining', 'Box ways', 'Rigid casting']
  },
  {
    image: acelm450,
    name: 'ACE LM 450',
    specs: ['Linear motion', 'Quick traverse', 'Precision control']
  },
  {
    image: acelm600,
    name: 'ACE LM 600',
    specs: ['Large capacity', 'Heavy cutting', 'Stable performance']
  },
  {
    image: cylindricalGrinding,
    name: 'Cylindrical Grinding Machine',
    specs: ['Surface finishing', 'Diameter control', 'Multiple operations']
  },
  {
    image: heavyDutyCylindrical,
    name: 'Heavy Duty Cylindrical Grinding Machine',
    specs: ['Large workpiece capacity', 'Precision grinding', 'Robust design']
  },
  {
    image: heavyDutySurface,
    name: 'Heavy Duty Surface Grinder Machine',
    specs: ['Flat surface grinding', 'Auto feed', 'Precision control']
  },
  {
    image: hydralicPress,
    name: 'Hydraulic Press Machine',
    specs: ['High pressure capacity', 'Safety controls', 'Multiple operations']
  }
];

const facilities = [
  {
    title: "CNC Machining Center",
    description: "State-of-the-art CNC machines for precision manufacturing",
    details: [
      "5-axis CNC machining capabilities",
      "High-speed machining centers",
      "Automated tool changing system",
      "Real-time quality monitoring"
    ],
    icon: <PrecisionManufacturingIcon sx={{ fontSize: 60 }} />,
    color: "#1a472a"
  },
  {
    title: "Quality Control Lab",
    description: "Advanced metrology and testing equipment",
    details: [
      "CMM measurement systems",
      "Surface roughness testers",
      "Material testing facilities",
      "Digital inspection tools"
    ],
    icon: <ScienceIcon sx={{ fontSize: 60 }} />,
    color: "#2196f3"
  },
  {
    title: "Assembly Unit",
    description: "Dedicated assembly lines for complex components",
    details: [
      "Modular assembly stations",
      "Electronic testing facilities",
      "Automated assembly systems",
      "Quality verification points"
    ],
    icon: <BuildIcon sx={{ fontSize: 60 }} />,
    color: "#ff9800"
  }
];

const cleanRoomFeatures = [
  {
    title: "Class 100,000 Clean Room",
    icon: <CleaningServicesIcon />,
    description: "ISO certified clean room for precision assembly",
    specs: [
      "Controlled particle count",
      "HEPA filtration system",
      "Airlock entry system",
      "Temperature controlled environment"
    ],
    color: "#4caf50"
  },
  {
    title: "Environmental Controls",
    icon: <SpeedIcon />,
    description: "Advanced environmental monitoring and control systems",
    specs: [
      "Real-time temperature monitoring",
      "Humidity control systems",
      "Pressure differential monitoring",
      "Air quality sensors"
    ],
    color: "#2196f3"
  },
  {
    title: "Quality Protocols",
    icon: <SecurityIcon />,
    description: "Strict quality control measures for clean room operations",
    specs: [
      "Personnel training protocols",
      "Clean room garment procedures",
      "Material transfer protocols",
      "Regular environmental testing"
    ],
    color: "#ff9800"
  }
];

const Infrastructure = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeSection, setActiveSection] = useState(null);
  const [hoveredMachine, setHoveredMachine] = useState(null);

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Hero Section with Icon */}
      <Box sx={{ mb: 0, textAlign: 'center', position: 'relative' }}>
        <Box sx={{
          position: 'absolute',
          top: -80,
          right: 0,
          opacity: 0.1,
          transform: 'rotate(-15deg)'
        }}>
          <FactoryIcon sx={{
            fontSize: 180,
            color: '#1a472a'
          }} />
        </Box>
        <Box sx={{ position: 'absolute', top: -80, left: 0, opacity: 0.1, transform: 'rotate(15deg)' }}>
          <FactoryIcon sx={{
            fontSize: 180,
            color: '#1a472a'
          }} />
        </Box>
        <Typography
          variant="h2"
          sx={{
            color: '#1a472a',
            fontWeight: 'bold',
            mb: 3,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -16,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #1a472a 0%, #4caf50 100%)',
              borderRadius: '2px',
            }
          }}
        >
          World-Class Manufacturing Infrastructure
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: 'text.secondary',
            maxWidth: '800px',
            mx: 'auto',
            mb: 1,
            lineHeight: 1.8
          }}
        >
          Experience excellence through our state-of-the-art facilities equipped with the latest technology and automated systems.
        </Typography>
      </Box>
      <Box sx={{ width: "600px ", height: "600px", margin: "0 auto" }}>
        <DotLottieReact
          src="https://lottie.host/f07ffe82-c801-46eb-bfcc-368ac7c05768/iR7hMklyow.lottie"
          autoplay
          loop
        />

      </Box>

      {/* Machine Gallery Section */}
      <Box sx={{ mb: 12 }}>
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            mb: 6,
            color: '#1a472a',
            fontWeight: 'bold',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -16,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #1a472a 0%, #4caf50 100%)',
              borderRadius: '2px',
            }
          }}
        >
          Advanced Machinery
        </Typography>
        <Typography
          variant="h5"
          sx={{
            textAlign: 'center',
            mb: 6,
            color: 'text.secondary',
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          Experience our state-of-the-art machinery equipped with the latest technology for precision manufacturing
        </Typography>
        <Grid container spacing={3}>
          {machines.map((machine, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                <Card
                  onMouseEnter={() => setHoveredMachine(index)}
                  onMouseLeave={() => setHoveredMachine(null)}
                  sx={{
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    transform: hoveredMachine === index ? 'scale(1.03)' : 'none',
                    boxShadow: hoveredMachine === index ? '0 8px 40px rgba(0,0,0,0.2)' : '0 2px 10px rgba(0,0,0,0.1)',
                    borderRadius: '16px',
                    '&:hover': {
                      '& .machine-image': {
                        transform: 'scale(1.1)',
                      },
                      '& .machine-overlay': {
                        opacity: 1,
                      },
                      '& .machine-title': {
                        color: '#1a472a',
                      }
                    }
                  }}
                >
                  <Box sx={{ position: 'relative', pt: '75%', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      image={machine.image}
                      alt={machine.name}
                      className="machine-image"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.6s ease',
                      }}
                    />
                    <Box
                      className="machine-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(26, 71, 42, 0.85)',
                        color: 'white',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        p: 3,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                          fontWeight: 'bold',
                          textAlign: 'center',
                          color: '#fff',
                          textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                      >
                        Key Features
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {machine.specs.map((spec, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: '#fff',
                              '&::before': {
                                content: '"✓"',
                                color: '#4caf50',
                                fontWeight: 'bold',
                                mr: 1,
                                fontSize: '1.2em'
                              }
                            }}
                          >
                            {spec}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                  <CardContent sx={{
                    bgcolor: '#f5f5f5',
                    borderTop: '1px solid rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease'
                  }}>
                    <Typography
                      variant="h6"
                      className="machine-title"
                      sx={{
                        fontWeight: 'bold',
                        textAlign: 'center',
                        transition: 'color 0.3s ease',
                        fontSize: '1.1rem',
                        lineHeight: 1.4,
                        color: 'text.primary'
                      }}
                    >
                      {machine.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Manufacturing Facilities Section */}
      <Box sx={{ mb: 12 }}>
        <Box sx={{ position: 'relative' }}>
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              mb: 6,
              color: '#1a472a',
              fontWeight: 'bold'
            }}
          >
            Manufacturing Excellence
          </Typography>
          <Grid container spacing={4}>
            {facilities.map((facility, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Zoom in={true} style={{ transitionDelay: `${index * 200}ms` }}>
                  <Card
                    onMouseEnter={() => setActiveSection(index)}
                    onMouseLeave={() => setActiveSection(null)}
                    sx={{
                      height: '100%',
                      transition: 'all 0.3s ease',
                      transform: activeSection === index ? 'scale(1.02)' : 'none',
                      boxShadow: activeSection === index ? 8 : 1,
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: facility.color,
                      }
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          mb: 3,
                          color: facility.color,
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        {facility.icon}
                        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 2, textAlign: 'center' }}>
                          {facility.title}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
                        {facility.description}
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        {facility.details.map((detail, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 1,
                              color: 'text.secondary',
                              '&::before': {
                                content: '"•"',
                                color: facility.color,
                                fontSize: '1.5em',
                                mr: 1
                              }
                            }}
                          >
                            {detail}
                          </Typography>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Clean Room Section with Lottie */}
      <Box sx={{ mb: 8, position: 'relative' }}>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(26, 71, 42, 0.05)',
            borderRadius: 4,
            zIndex: -1
          }}
        />
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            mb: 6,
            color: '#1a472a',
            fontWeight: 'bold',
            position: 'relative',
            pt: 4
          }}
        >
          Clean Room Assembly
        </Typography>
        <Grid container spacing={4} sx={{ px: 2, pb: 4 }}>
          {cleanRoomFeatures.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Fade in={true} style={{ transitionDelay: `${index * 200}ms` }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: 8,
                      '& .feature-icon': {
                        transform: 'rotate(360deg)',
                      }
                    },
                    background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
                  }}
                >
                  <Box
                    className="feature-icon"
                    sx={{
                      color: feature.color,
                      transition: 'transform 0.6s ease',
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center'
                    }}
                  >
                    {React.cloneElement(feature.icon, { sx: { fontSize: 48 } })}
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', textAlign: 'center' }}>
                    {feature.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    {feature.specs.map((spec, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 1,
                          color: 'text.secondary',
                          '&::before': {
                            content: '"✓"',
                            color: feature.color,
                            fontWeight: 'bold',
                            mr: 1
                          }
                        }}
                      >
                        {spec}
                      </Typography>
                    ))}
                  </Box>
                </Paper>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Key Statistics with Interactive Elements */}
      <Box sx={{ textAlign: 'center', py: 5, position: 'relative' }}>
        <Typography 
          variant="h3" 
          sx={{ 
            textAlign: 'center',
            mb: 6,
            color: '#1a472a',
            fontWeight: 'bold',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -16,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #1a472a 0%, #4caf50 100%)',
              borderRadius: '2px',
            }
          }}
        >
          Our Impact in Numbers
        </Typography>
        
        <Grid container spacing={4} sx={{ position: 'relative' }}>
          {[
            { number: '4+', label: 'Years of Excellence', icon: '🏆' },
            { number: '30+', label: 'Advanced Machines', icon: '⚙️' },
            { number: '50+', label: 'Projects Delivered', icon: '📦' },
            { number: '100%', label: 'Quality Assurance', icon: '✓' }
          ].map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Zoom in={true} style={{ transitionDelay: `${index * 200}ms` }}>
                <Card 
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
                    borderRadius: '16px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                      '& .stat-number': {
                        color: '#1a472a'
                      }
                    }
                  }}
                >
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      fontSize: '3rem',
                      mb: 1,
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    className="stat-number"
                  >
                    {stat.icon}
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: '#1a472a',
                      mb: 1
                    }}
                    className="stat-number"
                  >
                    {stat.number}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'text.secondary',
                      fontWeight: 500
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Final Section - Manufacturing Excellence */}
      <Box 
        sx={{ 
          py: 6,
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
          borderRadius: '40px',
          mt: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: '#1a472a',
                    fontWeight: 'bold',
                    mb: 3,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -16,
                      left: 0,
                      width: 100,
                      height: 4,
                      background: 'linear-gradient(90deg, #1a472a 0%, #4caf50 100%)',
                      borderRadius: '2px',
                    }
                  }}
                >
                  Revolutionizing Manufacturing
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'text.secondary',
                    mb: 4,
                    lineHeight: 1.8
                  }}
                >
                  Our automated production line represents the pinnacle of modern manufacturing excellence. With precision robotics and smart systems, we ensure:
                </Typography>
                <Grid container spacing={3}>
                  {[
                    { title: 'Automated Quality Control', desc: 'Real-time monitoring and inspection systems' },
                    { title: 'Smart Production', desc: 'IoT-enabled machinery for optimal performance' },
                    { title: 'Efficient Workflow', desc: 'Streamlined processes for faster delivery' },
                    { title: 'Zero Defect Policy', desc: 'Advanced error detection and prevention' }
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Card
                        sx={{
                          p: 2,
                          height: '100%',
                          background: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                            '& .feature-title': {
                              color: '#1a472a'
                            }
                          }
                        }}
                      >
                        <Typography 
                          variant="h6"
                          className="feature-title"
                          sx={{ 
                            fontWeight: 'bold',
                            mb: 1,
                            transition: 'color 0.3s ease'
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.desc}
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '450px',
                    height: '450px',
                    border: '2px dashed #1a472a',
                    borderRadius: '50%',
                    opacity: 0.1,
                    animation: 'spin 30s linear infinite'
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '350px',
                    height: '350px',
                    border: '2px dashed #4caf50',
                    borderRadius: '50%',
                    opacity: 0.1,
                    animation: 'spin-reverse 20s linear infinite'
                  },
                  '@keyframes spin': {
                    '0%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
                    '100%': { transform: 'translate(-50%, -50%) rotate(360deg)' }
                  },
                  '@keyframes spin-reverse': {
                    '0%': { transform: 'translate(-50%, -50%) rotate(360deg)' },
                    '100%': { transform: 'translate(-50%, -50%) rotate(0deg)' }
                  }
                }}
              >
                <Box 
                  sx={{ 
                    width: '400px',
                    height: '400px',
                    margin: '0 auto',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <DotLottieReact
                    src="https://lottie.host/c4aec854-9779-45ac-99a6-ce9fafec70ce/irSPxVeO1a.lottie"
                    loop
                    autoplay
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

    </Container>
  );
};

export default Infrastructure;
